import '@clr/icons';
import '@clr/icons/shapes/all-shapes';
import './Main.css';

import React, { useCallback, useContext,useMemo, Suspense } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { AllowedAccessMapContext } from 'vrlcm-gui-utils';
import Dashboard from './dashboard/Dashboard';
import { LCMApp } from 'vrlcm-gui-types';
import { ClockLoader, withAppRoot } from 'vrlcm-gui-components';
import Notifications from './notifications/Notifications';
import { l10n } from 'i18n/i18nUtil';

const spec: {
  name: string;
  appId: LCMApp;
} = {
  name: 'VMware Aria Suite Lifecycle',
  appId: 'shell'
};

const getAppSpec = (path: string) => {
  let appId = '';
  if (path.includes('lcops')) appId = 'lcops';
  else if (path.includes('locker')) appId = 'locker';
  else if (path.includes('marketplace')) appId = 'marketplace';
  else if (path.includes('authzn')) appId = 'authzn';
  else if (path.includes('crepo')) appId = 'crepo';
  else if (path.includes('requestchart')) appId = 'requestchart';
  else if (path.includes('cloud')) appId = 'cloud';

  return appId
    ? {
        name: l10n(`constants.appName.${appId}`),
        basepath: `/lcm/${appId}`
      }
    : spec;
};

const MainContent: React.FC = () => {
  const isConnect = process.env.REACT_APP_IS_CONNECT === 'true';

  const renderDashboard = () => <Dashboard />;

  let path = useLocation().pathname;
  const useShlConnect = path.indexOf('lcm/requestchart') === -1;
  const history = useHistory();

  const Locker = useMemo(() => React.lazy(() => import('./locker/components/Main')), []);
  const LCOPS = useMemo(() => React.lazy(() => import('./lcops/components/Main')), []);
  const MarketplaceShell = useMemo(() => React.lazy(() => import('./marketplace/components/Main')), []);
  const Authzn = useMemo(() => React.lazy(() => import('./authzn/components/Main')), []);
  const Crepo = useMemo(() => React.lazy(() => import('./crepo/components/Main')), []);
  const DBService = useMemo(() => React.lazy(() => import('./dbservice/components/Main')), []);
  const RequestChart = useMemo(() => React.lazy(() => import('./requestchart/components/Main')), []);
  const Cloud = useMemo(() => React.lazy(() => import('./cloud/components/Main')), []);
  const Migrate = useMemo(() => React.lazy(() => import('./migrate/components/Main')), []);

  const renderLocker = () => {
    return (
      <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
        <Locker />
      </Suspense>
    );
  };

  const renderLCOPS = () => {
    return (
      <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
        <LCOPS />
      </Suspense>
    );
  };

  const renderMarketplace = () => (
    <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
      <MarketplaceShell />
    </Suspense>
  );

  const renderAuthzn = () => (
    <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
      <Authzn />
    </Suspense>
  );

  const renderCrepo = () => (
    <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
      <Crepo />
    </Suspense>
  );

  const renderDBService = () => (
    <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
      <DBService />
    </Suspense>
  );

  const renderRequestChart = () => (
    <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
      <RequestChart />
    </Suspense>
  );

  const renderCloud = () => (
    <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
      <Cloud />
    </Suspense>
  );
  const renderMigrate = () => (
    <Suspense fallback={<ClockLoader text={l10n('loadingPleaseWait')} />}>
      <Migrate />
    </Suspense>
  );

  const handleRef = () => {
    return (ref: any) => {
      if (ref) {
        ref.spec = getAppSpec(path);
        ref.redirectTo = (path: string) => history.push(path);
      }
    };
  };

  const onUpdatedNotificationByUser = useCallback(() => {
    /** */
  }, []);

  const renderNotificationList = useCallback(() => {
    return (
      <Notifications updatedNotificationByUser={onUpdatedNotificationByUser} />
    );
  }, [onUpdatedNotificationByUser]);

  const allowedAccessMap = useContext(AllowedAccessMapContext);

  const skipToContent = () => {
    const focusEls = document.getElementById('mainContent')?.querySelectorAll(
      // eslint-disable-next-line max-len
      'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
    );
    focusEls && focusEls[0] && (focusEls[0] as HTMLElement).focus();
  };

  return (
    <>
      <button onClick={skipToContent} className="skip-link">
        {l10n('skipToContent')}
      </button>
      {isConnect && useShlConnect && <shl-connect ref={handleRef()} />}
      <div id="mainContent">
        <Switch>
          <Route path="/dashboard" exact={true} render={renderDashboard} />
          {allowedAccessMap['core.feature.notifications'] && (
            <Route path="/notifications" render={renderNotificationList} />
          )}
          <Route path="/lcm/locker" render={renderLocker} />
          <Route path="/lcm/lcops" render={renderLCOPS} />
          <Route path="/lcm/marketplace" render={renderMarketplace} />
          <Route path="/lcm/authzn" render={renderAuthzn} />
          <Route path="/lcm/crepo" render={renderCrepo} />
          <Route path="/lcm/dbservice" render={renderDBService} />
          <Route path="/lcm/requestchart" render={renderRequestChart} />
          <Route path="/lcm/cloud" render={renderCloud} />
          <Route path="/lcm/migrate" render={renderMigrate} />
          <Redirect to="/dashboard" />
        </Switch>
      </div>

      <div
        hidden={true}
        aria-live="polite"
        id="aria-live-element"
        aria-atomic="true"></div>
    </>
  );
};

export default withAppRoot(MainContent, spec);
